import * as React from "react";
import { SectionShell } from "@/components/design-system/SectionShell";
import { FileInput } from "@/components/ui/file-input";

const code = `import { FileInput } from "@/components/ui/file-input";
import { useForm } from "@inertiajs/react";

const form = useForm<{ file: File | null }>({ file: null });

<div className="space-y-2">
  <label htmlFor="deck">Deck file</label>
  <FileInput
    id="deck"
    accept=".html,text/html"
    hint="The built .html deck, around 600 KB"
    value={form.data.file}
    onFileChange={(file) => form.setData("file", file)}
    invalid={!!errors.file}
  />
  {errors.file && <p className="text-xs text-danger-display">{errors.file}</p>}
</div>

// A File in useForm's data switches the request to multipart automatically —
// no forceFormData needed.
form.post("/admin/audits");`;

export function FileInputSection() {
  const [file, setFile] = React.useState<File | null>(null);
  const [invalidFile, setInvalidFile] = React.useState<File | null>(null);

  return (
    <SectionShell
      id="file-input"
      title="File input"
      description={
        <>
          A labelled drop area wrapping a visually hidden native{" "}
          <code>&lt;input type="file"&gt;</code>. Click or drag to choose;
          the selected file shows its name and size with a clear button.
          Controlled on a <code>File | null</code>, which is the shape Inertia's{" "}
          <code>useForm</code> expects.
        </>
      }
      whenToUse={
        <ul>
          <li>Any upload field — a native file input can't take the tokens.</li>
          <li>Single-file uploads where the chosen file should stay visible.</li>
          <li>
            Pair with a <code>&lt;label htmlFor&gt;</code> above it, like every
            other field.
          </li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Multi-file or folder uploads — this is deliberately single-file.</li>
          <li>
            Drag-and-drop reordering or image cropping — those need their own
            component.
          </li>
        </ul>
      }
      preview={
        <div className="grid max-w-3xl gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="ds-file">Deck file</label>
            <FileInput
              id="ds-file"
              accept=".html,text/html"
              hint="The built .html deck, around 600 KB"
              value={file}
              onFileChange={setFile}
            />
            <p className="text-xs text-ink-muted">
              Helper text sits under the field, as on any other input.
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="ds-file-invalid">Deck file (error state)</label>
            <FileInput
              id="ds-file-invalid"
              accept=".html,text/html"
              value={invalidFile}
              onFileChange={setInvalidFile}
              invalid
            />
            <p className="text-xs text-danger-display">
              Found 0 slides in that file — is this the deck HTML, or the PDF?
            </p>
          </div>
        </div>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>accept</code> mirrors the native attribute. It is a filter, not
            a guarantee — always validate server-side too.
          </li>
          <li>
            <code>invalid</code> reddens the border and sets{" "}
            <code>aria-invalid</code>; render the message in the same slot as
            helper text.
          </li>
          <li>
            <code>hint</code> puts a one-line expectation inside the empty state
            (size, format), which heads off the wrong-file upload.
          </li>
          <li>
            <code>disabled</code> dims the area and blocks both click and drop.
          </li>
          <li>
            Clearing resets the underlying input's value, so re-picking the same
            file still fires a change.
          </li>
        </ul>
      }
    />
  );
}
